import React, { useState } from 'react';
import { Download, Upload, Shield, RefreshCw, AlertTriangle, CheckCircle } from 'lucide-react';

const BackupManager = ({ user, onClose }) => {
  const [loading, setLoading] = useState(false);
  const [backupStatus, setBackupStatus] = useState('idle'); // idle, success, error
  const [restoreFile, setRestoreFile] = useState(null);
  const [restoreStatus, setRestoreStatus] = useState('idle');

  const createBackup = async () => {
    try {
      setLoading(true);
      setBackupStatus('idle');
      
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/backup/user-data?user_id=${user.id}`);
      
      if (!response.ok) {
        throw new Error('Erro ao gerar backup');
      }
      
      const backupData = await response.json();
      
      // Create and download backup file
      const dataStr = JSON.stringify(backupData, null, 2);
      const dataBlob = new Blob([dataStr], { type: 'application/json' });
      
      const link = document.createElement('a');
      link.href = URL.createObjectURL(dataBlob);
      link.download = `stake-mind-backup-${new Date().toISOString().split('T')[0]}.json`;
      link.click();
      
      setBackupStatus('success');
      
    } catch (error) {
      console.error('Backup error:', error);
      setBackupStatus('error');
    } finally {
      setLoading(false);
    }
  };

  const handleFileSelect = (event) => {
    const file = event.target.files[0];
    if (file && file.type === 'application/json') {
      setRestoreFile(file);
      setRestoreStatus('idle');
    } else {
      alert('❌ Por favor, selecione um arquivo JSON válido.');
    }
  };

  const restoreBackup = async () => {
    if (!restoreFile) return;
    
    if (!confirm('⚠️ ATENÇÃO: Esta ação irá substituir todos os seus dados atuais pelos dados do backup. Deseja continuar?')) {
      return;
    }
    
    try {
      setLoading(true);
      setRestoreStatus('idle');
      
      const fileContent = await restoreFile.text();
      const backupData = JSON.parse(fileContent);
      
      // Validate backup structure
      if (!backupData.backup_info || !backupData.user || backupData.backup_info.user_id !== user.id) {
        throw new Error('Arquivo de backup inválido ou não pertence a este usuário');
      }
      
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/backup/restore?user_id=${user.id}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(backupData)
      });
      
      if (!response.ok) {
        throw new Error('Erro ao restaurar backup');
      }
      
      const result = await response.json();
      setRestoreStatus('success');
      
      alert(`✅ Backup restaurado com sucesso!\n\nItens restaurados:\n• ${result.restored_items.bets} apostas\n• ${result.restored_items.expenses} despesas\n• ${result.restored_items.groups} grupos\n• ${result.restored_items.betting_houses} casas de apostas\n• ${result.restored_items.goals} metas\n\nRecarregue a página para ver as alterações.`);
      
    } catch (error) {
      console.error('Restore error:', error);
      setRestoreStatus('error');
      alert('❌ Erro ao restaurar backup: ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  const scheduleAutoBackup = () => {
    // Set reminder to create backup weekly
    const lastBackup = localStorage.getItem('last_backup_date');
    const now = new Date();
    const oneWeekAgo = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);
    
    if (!lastBackup || new Date(lastBackup) < oneWeekAgo) {
      setTimeout(() => {
        if (confirm('📅 Lembrete: Que tal fazer um backup dos seus dados? É rápido e mantém suas informações seguras!')) {
          createBackup();
        }
      }, 2000); // Show reminder after 2 seconds
    }
  };

  React.useEffect(() => {
    // Check if auto backup reminder should be shown
    scheduleAutoBackup();
  }, []);

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('pt-BR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white dark:bg-gray-800 rounded-xl p-6 w-full max-w-md max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between mb-6">
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white flex items-center">
            <Shield className="w-5 h-5 mr-2 text-green-600" />
            Backup dos Dados
          </h3>
          <button
            onClick={onClose}
            className="text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"
          >
            ✕
          </button>
        </div>

        <div className="space-y-6">
          {/* Backup Info */}
          <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-4">
            <div className="flex items-start space-x-3">
              <Shield className="w-5 h-5 text-blue-600 mt-0.5" />
              <div>
                <h4 className="font-medium text-blue-900 dark:text-blue-200">Por que fazer backup?</h4>
                <p className="text-sm text-blue-700 dark:text-blue-300 mt-1">
                  Mantenha seus dados seguros e restaure-os a qualquer momento. O backup inclui apostas, despesas, grupos, metas e configurações.
                </p>
              </div>
            </div>
          </div>

          {/* Create Backup */}
          <div>
            <h4 className="font-medium text-gray-900 dark:text-white mb-3">💾 Criar Backup</h4>
            <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
              Gere um arquivo com todos os seus dados para guardar em local seguro.
            </p>
            
            <button
              onClick={createBackup}
              disabled={loading}
              className="w-full flex items-center justify-center space-x-2 px-4 py-3 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {loading ? (
                <RefreshCw className="w-5 h-5 animate-spin" />
              ) : (
                <Download className="w-5 h-5" />
              )}
              <span>{loading ? 'Gerando Backup...' : 'Baixar Backup'}</span>
            </button>
            
            {backupStatus === 'success' && (
              <div className="mt-3 flex items-center space-x-2 text-green-600 dark:text-green-400">
                <CheckCircle className="w-4 h-4" />
                <span className="text-sm">Backup criado com sucesso!</span>
              </div>
            )}
            
            {backupStatus === 'error' && (
              <div className="mt-3 flex items-center space-x-2 text-red-600 dark:text-red-400">
                <AlertTriangle className="w-4 h-4" />
                <span className="text-sm">Erro ao criar backup. Tente novamente.</span>
              </div>
            )}
          </div>

          {/* Restore Backup */}
          <div className="border-t border-gray-200 dark:border-gray-600 pt-6">
            <h4 className="font-medium text-gray-900 dark:text-white mb-3">📤 Restaurar Backup</h4>
            
            <div className="bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-lg p-4 mb-4">
              <div className="flex items-start space-x-3">
                <AlertTriangle className="w-5 h-5 text-yellow-600 mt-0.5" />
                <div>
                  <h5 className="font-medium text-yellow-800 dark:text-yellow-200">⚠️ Atenção</h5>
                  <p className="text-sm text-yellow-700 dark:text-yellow-300 mt-1">
                    Restaurar um backup irá <strong>substituir todos os dados atuais</strong>. Use apenas arquivos de backup válidos.
                  </p>
                </div>
              </div>
            </div>
            
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Selecionar Arquivo de Backup (.json)
                </label>
                <input
                  type="file"
                  accept=".json"
                  onChange={handleFileSelect}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white file:mr-4 file:py-2 file:px-4 file:rounded-full file:border-0 file:text-sm file:font-semibold file:bg-blue-50 file:text-blue-700 hover:file:bg-blue-100"
                />
              </div>
              
              {restoreFile && (
                <div className="bg-gray-50 dark:bg-gray-700/50 rounded-lg p-3">
                  <p className="text-sm text-gray-600 dark:text-gray-400">
                    <strong>Arquivo selecionado:</strong> {restoreFile.name}
                  </p>
                  <p className="text-sm text-gray-500 dark:text-gray-400">
                    Tamanho: {(restoreFile.size / 1024).toFixed(1)} KB
                  </p>
                </div>
              )}
              
              <button
                onClick={restoreBackup}
                disabled={!restoreFile || loading}
                className="w-full flex items-center justify-center space-x-2 px-4 py-3 bg-orange-600 text-white rounded-lg hover:bg-orange-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {loading ? (
                  <RefreshCw className="w-5 h-5 animate-spin" />
                ) : (
                  <Upload className="w-5 h-5" />
                )}
                <span>{loading ? 'Restaurando...' : 'Restaurar Backup'}</span>
              </button>
            </div>
            
            {restoreStatus === 'success' && (
              <div className="mt-3 flex items-center space-x-2 text-green-600 dark:text-green-400">
                <CheckCircle className="w-4 h-4" />
                <span className="text-sm">Backup restaurado com sucesso!</span>
              </div>
            )}
            
            {restoreStatus === 'error' && (
              <div className="mt-3 flex items-center space-x-2 text-red-600 dark:text-red-400">
                <AlertTriangle className="w-4 h-4" />
                <span className="text-sm">Erro ao restaurar backup.</span>
              </div>
            )}
          </div>

          {/* Auto Backup Info */}
          <div className="border-t border-gray-200 dark:border-gray-600 pt-6">
            <h4 className="font-medium text-gray-900 dark:text-white mb-3">🔄 Backup Automático</h4>
            <div className="bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg p-4">
              <div className="flex items-start space-x-3">
                <RefreshCw className="w-5 h-5 text-green-600 mt-0.5" />
                <div>
                  <h5 className="font-medium text-green-800 dark:text-green-200">Lembretes Ativos</h5>
                  <p className="text-sm text-green-700 dark:text-green-300 mt-1">
                    O sistema lembrará você de fazer backup semanalmente para manter seus dados sempre seguros.
                  </p>
                  <p className="text-xs text-green-600 dark:text-green-400 mt-2">
                    💡 Último backup: {localStorage.getItem('last_backup_date') 
                      ? formatDate(localStorage.getItem('last_backup_date'))
                      : 'Nunca'}
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default BackupManager;